import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetApiV1MissionsMissionIdQueryKey,
  useGetApiV1MissionsMissionId,
  usePostApiV1MissionsMissionIdMembersMembershipIdKnight,
  usePostApiV1MissionsMissionIdMembersMembershipIdParticipation,
  usePostApiV1MissionsMissionIdMembersMembershipIdReinstate,
  usePostApiV1MissionsMissionIdReportApprove,
  usePutApiV1MissionsMissionIdReport,
  usePutApiV1MissionsMissionIdRoundsRoundIdReport,
} from '@/shared/api/generated/missions/missions';
import type {
  MissionDetailDto,
  MissionEventDto,
  MissionMemberDto,
  MissionRoundDto,
} from '@/shared/api/generated/model';
import { problemDetail } from '@/shared/api/problem';
import { SystemRoles, useRoles } from '@/features/auth/model/use-roles';
import { MissionChat } from '@/features/missions/ui/MissionChat';
import { MissionControl } from '@/features/missions/ui/MissionControl';
import { MissionControlActions } from '@/features/missions/ui/MissionControlActions';
import { useLocale } from '@/shared/lib/i18n/locale-context';
import { formatDateTime, formatDuration } from '@/shared/lib/format/dates';
import { QueryError } from '@/shared/ui/QueryError';
import { Chip, toneForMissionStatus } from '@/shared/ui/Chip';
import { Button } from '@/shared/ui/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { MissionCoverage } from '@/features/missions/ui/MissionCoverage';
import { Panel } from '@/shared/ui/Panel';
import { ReportFileLink } from '@/features/missions/ui/ReportFileLink';
import { MissionReportDialog } from '@/features/missions/ui/MissionReportDialog';
import { LiveEvery, liveEvery } from '@/shared/lib/live';

/**
 * One mission in full (§23.1's control room).
 *
 * ⚠️ The member table is here because §23.6 asks for it by name: participation is credited
 * **manually by the Head of Control**, and what the system owes them to decide with is the time
 * spent, the distance covered, and the report. Those live on three tables, so the screen that shows
 * them together is the requirement — a board row cannot carry it.
 *
 * The members are shown unfiltered, including removed ones, which is the opposite of the board. The
 * board counts who is on a mission; this is where a removal is reviewed, and filtering would hide
 * exactly the rows the screen exists for.
 */
export function MissionDetail({ missionId }: { missionId: string }) {
  const { t } = useLocale();
  const detail = useGetApiV1MissionsMissionId(missionId, liveEvery(LiveEvery.work));

  if (detail.isPending) {
    return <p className="text-sm text-muted-foreground">{t.app.loading}</p>;
  }

  if (detail.isError) {
    return <QueryError error={detail.error} onRetry={() => void detail.refetch()} />;
  }

  return <Loaded detail={detail.data} />;
}

function Loaded({ detail }: { detail: MissionDetailDto }) {
  const { t, locale } = useLocale();

  return (
    <section className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <Link to="/missions" className="text-sm text-primary underline underline-offset-4">
          {t.missions.detailBack}
        </Link>

        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight">{detail.title}</h1>
          <Chip tone={toneForMissionStatus(detail.status)}>
            {label(detail.status, t.missionStatus)}
          </Chip>
          <span className="text-sm text-muted-foreground">
            {label(detail.type, t.missionType)}
          </span>
        </div>

        <p className="text-sm text-muted-foreground">
          {detail.region || t.missions.noRegion}
          {detail.city ? ` — ${detail.city}` : ''} · {detail.areaKm2.toFixed(1)}{' '}
          {t.missions.areaUnit}
        </p>
      </header>

      <Panel title={t.missions.detailTimes}>
        <dl className="grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-3">
          <Figure label={t.missions.detailIncidentAt} value={when(detail.incidentAt, locale, t)} />
          <Figure label={t.missions.created} value={when(detail.createdAt, locale, t)} />
          <Figure label={t.missions.detailApprovedAt} value={when(detail.approvedAt, locale, t)} />
          <Figure label={t.missions.detailStartedAt} value={when(detail.startedAt, locale, t)} />
          <Figure label={t.missions.detailClosedAt} value={when(detail.closedAt, locale, t)} />
          <Figure
            label={t.missions.detailOutcome}
            value={detail.outcome ? label(detail.outcome, t.missionOutcome) : t.missions.detailNone}
          />
          <Figure
            label={t.missions.detailVisibilityRadius}
            value={`${detail.visibilityRadiusM} ${t.missions.metres}`}
          />
          <Figure
            label={t.missions.detailIdleThreshold}
            // Always metres on the wire: §23.5 lets the Head of Control choose the unit, and that
            // is a display decision the server does not store.
            value={
              detail.idleThresholdM == null
                ? t.missions.detailNone
                : `${detail.idleThresholdM} ${t.missions.metres}`
            }
          />
          <Figure
            label={t.missions.detailNotificationScope}
            // ⚠️ Translated, not printed raw — «AllUsers» was rendered as-is in an Arabic sentence.
            // Falls back to the server's own spelling if a scope is added there before it is added
            // here: an unknown word is better than an empty box on a decision the operator made.
            value={
              t.notificationScope[detail.notificationScope as keyof typeof t.notificationScope] ??
              detail.notificationScope
            }
          />
        </dl>
      </Panel>

      {/* ⚠️ High on the page, above the committee tally and the members. The first question an
          operator opening a mission has is who is answerable for it — and the answer is sometimes
          "nobody", which is the one that has to be seen. */}
      <MissionControl missionId={detail.missionId} />

      {/* Present only while the committee still holds the mission. A tally beside an approved
          mission reads as the reason it was approved — and §23.2 lets the Head of Control publish
          with no vote at all, so on many approved missions that reading would be false. */}
      {detail.committee ? (
        <Panel title={t.missions.detailCommittee}>
          <dl className="grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-4">
            <Figure
              label={t.missions.detailApprovals}
              value={`${detail.committee.approvals} / ${detail.committee.requiredApprovals}`}
            />
            <Figure label={t.missions.detailRejections} value={String(detail.committee.rejections)} />
            <Figure
              label={t.missions.detailAbstentions}
              value={String(detail.committee.abstentions)}
            />
            <Figure
              label={t.missions.detailDeadline}
              value={when(detail.committee.deadlineAt, locale, t)}
            />
          </dl>

          {detail.committee.awaitingEscalation ? (
            <p className="text-sm text-warning">{t.missions.detailAwaitingEscalation}</p>
          ) : null}
        </Panel>
      ) : null}

      <Panel title={t.missions.detailMembers}>
        {detail.members.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t.missions.detailNoMembers}</p>
        ) : (
          <Table className="min-w-3xl">
            <TableHeader>
              <TableRow className="bg-surface-elevated text-xs uppercase tracking-wider text-muted-foreground">
                <TableHead scope="col">{t.missions.detailMemberName}</TableHead>
                <TableHead scope="col">{t.missions.detailMemberState}</TableHead>
                <TableHead scope="col">{t.missions.detailMemberSector}</TableHead>
                <TableHead scope="col">{t.missions.detailMemberJoined}</TableHead>
                <TableHead scope="col">{t.missions.detailMemberDuration}</TableHead>
                <TableHead scope="col">{t.missions.detailMemberDistance}</TableHead>
                <TableHead scope="col">{t.missions.detailMemberCounted}</TableHead>
                <TableHead scope="col">{t.missions.detailPoints}</TableHead>
                <TableHead scope="col">{t.missions.detailKnight}</TableHead>
                <TableHead scope="col">{t.missions.actions}</TableHead>

                {/* §23.5's manual reinstatement, in its own column: it is a correction to an
                    automatic removal, not one of the judgements the actions column holds. */}
                <TableHead scope="col">{t.missions.detailReinstate}</TableHead>

                {/* ⚠️ Its own column too, and for a different reason: these are what THIS mission's
                    control may do to its team while the search runs (decision of 22 August 2026) —
                    not the Head of Control's judgements beside them, and not a correction. */}
                <TableHead scope="col">{t.missions.detailLeaderColumn}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {detail.members.map((member) => (
                <MemberRow
                  key={member.membershipId}
                  member={member}
                  missionId={detail.missionId}
                  missionType={detail.type}
                  missionStatus={detail.status}
                  missionOutcome={detail.outcome}
                  locale={locale}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      <MissionRounds detail={detail} locale={locale} />

      <Panel title={t.missions.coverageHeading}>
        <MissionCoverage
          missionId={detail.missionId}
          area={detail.area}
          sectors={detail.sectors}
          watched={watchedMembers(detail)}
        />
      </Panel>

      {/* ⚠️ Above the report and the timeline, below the map. The conversation is what is happening
          NOW; the two panels under it are the record of what already has. An operator opening this
          screen mid-search is looking for the first of those. */}
      <MissionChat missionId={detail.missionId} />

      <MissionReport detail={detail} />

      <Panel title={t.missions.detailTimeline}>
        {detail.timeline.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t.missions.detailNoTimeline}</p>
        ) : (
          <ol className="flex flex-col gap-2">
            {detail.timeline.map((event, index) => (
              <TimelineRow key={`${event.occurredAt}-${index}`} event={event} locale={locale} />
            ))}
          </ol>
        )}
      </Panel>
    </section>
  );
}

/**
 * The closure report, with the two acts §3 keeps separate: preparing it and signing it.
 *
 * Desk Control writes; only the Head of Control approves. §23.6 makes them the one who credits
 * participation and names the report as the evidence they decide on — so the text they credit
 * against is text they signed.
 */
function MissionReport({ detail }: { detail: MissionDetailDto }) {
  const { t, locale } = useLocale();
  const { hasAny } = useRoles();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    setError(null);
    setEditing(false);
    await queryClient.invalidateQueries({
      queryKey: getGetApiV1MissionsMissionIdQueryKey(detail.missionId),
    });
  };

  const onError = (cause: unknown) => setError(problemDetail(cause) ?? t.missions.actionFailed);

  const write = usePutApiV1MissionsMissionIdReport({ mutation: { onSuccess: refresh, onError } });
  const approve = usePostApiV1MissionsMissionIdReportApprove({
    mutation: { onSuccess: refresh, onError },
  });

  const report = detail.report;
  const isApproved = Boolean(report?.approvedAt);

  // The report is written after closure — the outcome it carries does not exist until then, and the
  // server answers 409 to an earlier attempt. The button follows the domain rather than offering a
  // click that is certain to fail.
  const canWrite =
    detail.status === 'Closed' &&
    !isApproved &&
    hasAny(SystemRoles.DeskControl, SystemRoles.HeadOfControl, SystemRoles.SystemAdministrator);

  const canApprove =
    report != null && !isApproved && hasAny(SystemRoles.HeadOfControl, SystemRoles.SystemAdministrator);

  return (
    <Panel title={t.missions.detailReport}>
      {report == null ? (
        <p className="text-sm text-muted-foreground">{t.missions.detailNoReport}</p>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="whitespace-pre-wrap text-sm">{report.summary}</p>

          <dl className="grid grid-cols-2 gap-x-8 gap-y-2 sm:grid-cols-3">
            <Figure
              label={t.missions.detailReportPreparedBy}
              value={report.preparedByName ?? t.missions.detailNone}
            />
            <Figure
              label={t.missions.detailReportApprovedBy}
              value={report.approvedByName ?? t.missions.detailReportPending}
            />
            <Figure
              label={t.missions.detailOutcome}
              value={label(report.outcome, t.missionOutcome)}
            />
          </dl>

          {isApproved ? (
            <div className="flex flex-col gap-1">
              <p className="text-sm text-success">
                {t.missions.detailReportApproved} — {when(report.approvedAt, locale, t)}
              </p>

              {/* ⚠️ Only when the document actually exists. Approving is what renders it, so a
                  report signed before that existed carries no PDF — and offering a link that
                  resolves to nothing is worse than offering none. */}
              {report.hasPdf ? <ReportFileLink missionId={detail.missionId} /> : null}
            </div>
          ) : null}
        </div>
      )}

      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {canWrite ? (
          <Button variant="ghost" onClick={() => setEditing(true)}>
            {report == null ? t.missions.detailWriteReport : t.missions.detailReviseReport}
          </Button>
        ) : null}

        {canApprove ? (
          <Button onClick={() => approve.mutate({ missionId: detail.missionId })}>
            {t.missions.detailApproveReport}
          </Button>
        ) : null}

        {/* Stated rather than left as an absent button: an operator who cannot find the edit
            control needs to know the text is frozen, not that the panel is broken. */}
        {isApproved ? (
          <p className="text-xs text-muted-foreground">{t.missions.detailReportApprovedLocked}</p>
        ) : null}
      </div>

      <MissionReportDialog
        open={editing}
        onOpenChange={setEditing}
        initialSummary={report?.summary ?? ''}
        busy={write.isPending}
        serverError={error}
        onConfirm={(summary) =>
          write.mutate({ missionId: detail.missionId, data: { summary } })
        }
      />
    </Panel>
  );
}

function MemberRow({
  member,
  missionId,
  missionType,
  missionStatus,
  missionOutcome,
  locale,
}: {
  member: MissionMemberDto;
  missionId: string;
  missionType: string;
  missionStatus: string;
  missionOutcome: string | null | undefined;
  locale: 'ar' | 'en';
}) {
  const { t } = useLocale();

  return (
    <TableRow>
      <TableCell className="font-medium">
        {member.fullName}

        {/* Beside the name, because leading the mission is who they are to the team rather than
            something done to them — and the chat badges them the same way. */}
        {member.isLeader ? (
          <span className="block text-xs font-normal text-primary">{t.missions.detailLeader}</span>
        ) : null}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {/* ⚠️ Translated, not printed raw. This cell showed «Joined» and «Apologised» — the server's
            own enum spelling — in a table the control room reads during a search, which is the same
            defect already fixed for the mission status, the notification scope and the timeline.
            `label` falls back to the raw value, so a state added on the server before it is added
            here is shown honestly rather than blanked. */}
        {label(member.state, t.memberState)}
        {/* ⚠️ The idle warning is shown beside the removal, always. §23.5 removes members on four
            signals, one of which — no location update — is indistinguishable from a phone that lost
            signal in a wadi. When a removal is disputed the question is whether the warning fired,
            and a row showing only the removal cannot answer it. */}
        {member.idleWarnedAt ? (
          <span className="block text-xs text-warning">{t.missions.detailIdleWarned}</span>
        ) : null}
        {member.removedAt ? (
          <span className="block text-xs text-danger">
            {t.missions.detailRemoved}
            {member.removalReason ? ` — ${member.removalReason}` : ''}
          </span>
        ) : null}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {member.sectorLabelAr ?? t.missions.detailNone}
      </TableCell>
      <TableCell className="tabular text-muted-foreground">
        {formatDateTime(member.joinedAt, locale)}
      </TableCell>
      <TableCell className="tabular text-muted-foreground">
        {formatDuration(secondsToTimeSpan(member.activeSeconds), {
          day: t.missions.unitDay,
          hour: t.missions.unitHour,
          minute: t.missions.unitMinute,
        })}
      </TableCell>
      <TableCell className="tabular text-muted-foreground">
        {(member.distanceCoveredM / 1000).toFixed(2)} {t.missions.kilometres}
      </TableCell>
      <TableCell>
        <Chip tone={member.countedParticipation ? 'approved' : 'neutral'}>
          {member.countedParticipation ? t.missions.detailCounted : t.missions.detailNotCounted}
        </Chip>
      </TableCell>
      <TableCell className="tabular text-muted-foreground">
        {/* What this membership was ACTUALLY awarded, as the rule stood when it was credited — not
            what the rule is worth today. That distinction is the reason the figure is stored, and
            showing today's value here would quietly contradict the volunteer's total. */}
        {member.countedParticipation ? member.awardedPoints : t.missions.detailNone}
      </TableCell>
      <TableCell>
        <KnightAction
          member={member}
          missionId={missionId}
          missionType={missionType}
          missionStatus={missionStatus}
          missionOutcome={missionOutcome}
        />
      </TableCell>
      <TableCell>
        <ParticipationAction
          member={member}
          missionId={missionId}
          missionStatus={missionStatus}
        />
      </TableCell>
      <TableCell>
        <ReinstateAction member={member} missionId={missionId} missionStatus={missionStatus} />
      </TableCell>
      <TableCell>
        <MissionControlActions
          member={member}
          missionId={missionId}
          missionType={missionType}
          missionStatus={missionStatus}
        />
      </TableCell>
    </TableRow>
  );
}

/**
 * §23.5's manual reinstatement path — the other half of the two-minute warning.
 *
 * ⚠️ **§23.5 calls this correctness, not politeness**, in its own words: the sweep's third signal —
 * no location update — "is indistinguishable from a phone that lost signal in a wadi, which is the
 * normal condition this whole system is built around. Removing a member on that alone would remove
 * exactly the people doing the work."
 *
 * ⚠️ Anyone on control duty, not the Head of Control alone. A wrongly removed volunteer is standing
 * in the desert now, and a correction that waits for the one person who sets deadlines is a
 * correction that arrives after the search.
 */
function ReinstateAction({
  member,
  missionId,
  missionStatus,
}: {
  member: MissionMemberDto;
  missionId: string;
  missionStatus: string;
}) {
  const { t } = useLocale();
  const { hasAny } = useRoles();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const reinstate = usePostApiV1MissionsMissionIdMembersMembershipIdReinstate({
    mutation: {
      onSuccess: async () => {
        setError(null);
        await queryClient.invalidateQueries({
          queryKey: getGetApiV1MissionsMissionIdQueryKey(missionId),
        });
      },
      onError: (cause: unknown) => setError(problemDetail(cause) ?? t.missions.actionFailed),
    },
  });

  // ⚠️ Only for an idle removal. Somebody who left, apologised or was excluded went through a
  // different door, and this must not look like the way back through any of them.
  if (member.state !== 'IdleRemoved') {
    return <span className="text-muted-foreground">{t.missions.detailNone}</span>;
  }

  if (!hasAny(SystemRoles.DeskControl, SystemRoles.HeadOfControl, SystemRoles.SystemAdministrator)) {
    return <span className="text-muted-foreground">{t.missions.noActions}</span>;
  }

  // The server answers 409 on a mission that has ended: putting somebody back on a closed search
  // would place them on ground nobody is covering.
  if (missionStatus !== 'Active' && missionStatus !== 'Approved') {
    return <span className="text-muted-foreground">{t.missions.detailNone}</span>;
  }

  return (
    <div className="flex flex-col gap-1">
      <Button
        variant="ghost"
        disabled={reinstate.isPending}
        onClick={() =>
          reinstate.mutate({ missionId, membershipId: member.membershipId })
        }
      >
        {t.missions.detailReinstate}
      </Button>

      <span className="text-xs text-muted-foreground">{t.missions.detailReinstateHint}</span>

      {error ? <span className="text-xs text-danger">{error}</span> : null}
    </div>
  );
}

/**
 * §23.6's decision: participation is credited **manually by the Head of Control**.
 *
 * ⚠️ Reversible, and the withdraw button is not hidden once a credit is given. A manual judgement
 * that cannot be corrected is worse than no judgement — and the correction carries its own
 * attribution, so the record shows both acts rather than one silently overwritten flag.
 */
function ParticipationAction({
  member,
  missionId,
  missionStatus,
}: {
  member: MissionMemberDto;
  missionId: string;
  missionStatus: string;
}) {
  const { t } = useLocale();
  const { hasAny } = useRoles();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const credit = usePostApiV1MissionsMissionIdMembersMembershipIdParticipation({
    mutation: {
      onSuccess: async () => {
        setError(null);
        await queryClient.invalidateQueries({
          queryKey: getGetApiV1MissionsMissionIdQueryKey(missionId),
        });
      },
      onError: (cause: unknown) =>
        setError(problemDetail(cause) ?? t.missions.actionFailed),
    },
  });

  // §23.6 names the Head of Control. Desk Control works shifts, and a credit is a standing
  // judgement about a person rather than a shift action.
  if (!hasAny(SystemRoles.HeadOfControl, SystemRoles.SystemAdministrator)) {
    return <span className="text-muted-foreground">{t.missions.noActions}</span>;
  }

  // The server answers 409 before the mission ends — the evidence it is decided on is not final
  // while people are still walking. Said rather than left as a disabled button with no reason.
  if (missionStatus !== 'Closed' && missionStatus !== 'Cancelled') {
    return (
      <span className="text-xs text-muted-foreground">{t.missions.detailCreditPending}</span>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <Button
        variant="ghost"
        disabled={credit.isPending}
        onClick={() =>
          credit.mutate({
            missionId,
            membershipId: member.membershipId,
            data: { counted: !member.countedParticipation },
          })
        }
      >
        {member.countedParticipation
          ? t.missions.detailWithdrawParticipation
          : t.missions.detailCreditParticipation}
      </Button>
      {error ? (
        <span role="alert" className="text-xs text-danger">
          {error}
        </span>
      ) : null}
    </div>
  );
}

/**
 * §23.6's فارس: the person who found the missing person.
 *
 * ⚠️ A judgement, never a computation. Nothing here infers a candidate from a track or from who was
 * nearest the last known position — those are guesses about who found somebody, and the people on
 * the ground know.
 */
function KnightAction({
  member,
  missionId,
  missionType,
  missionStatus,
  missionOutcome,
}: {
  member: MissionMemberDto;
  missionId: string;
  missionType: string;
  missionStatus: string;
  missionOutcome: string | null | undefined;
}) {
  const { t } = useLocale();
  const { hasAny } = useRoles();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const award = usePostApiV1MissionsMissionIdMembersMembershipIdKnight({
    mutation: {
      onSuccess: async () => {
        setError(null);
        await queryClient.invalidateQueries({
          queryKey: getGetApiV1MissionsMissionIdQueryKey(missionId),
        });
      },
      onError: (cause: unknown) => setError(problemDetail(cause) ?? t.missions.actionFailed),
    },
  });

  // §23.6: the field leader or the Head of Control. The field leader is the ninth role, added on
  // the association's answer of 18 August 2026, and this award is the only permission it carries.
  const mayAward = hasAny(
    SystemRoles.FieldLeader,
    SystemRoles.HeadOfControl,
    SystemRoles.SystemAdministrator,
  );

  // Only where somebody actually found the missing person. On the other outcomes there is no finder
  // to name, and the server answers 409.
  const isAwardable =
    missionType === 'Missing' &&
    missionStatus === 'Closed' &&
    (missionOutcome === 'FoundAlive' || missionOutcome === 'FoundDeceased');

  if (member.isKnight) {
    return (
      <div className="flex flex-col gap-1">
        <Chip tone="approved">{t.missions.detailKnight}</Chip>
        {/* Withdrawing is never gated: a name given by mistake must be removable, or a wrong
            award is permanent. */}
        {mayAward ? (
          <Button
            variant="ghost"
            disabled={award.isPending}
            onClick={() =>
              award.mutate({
                missionId,
                membershipId: member.membershipId,
                data: { awarded: false },
              })
            }
          >
            {t.missions.detailWithdrawKnight}
          </Button>
        ) : null}
        {error ? (
          <span role="alert" className="text-xs text-danger">
            {error}
          </span>
        ) : null}
      </div>
    );
  }

  if (!mayAward) {
    return <span className="text-muted-foreground">{t.missions.detailNone}</span>;
  }

  if (!isAwardable) {
    return (
      <span className="text-xs text-muted-foreground">{t.missions.detailKnightUnavailable}</span>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <Button
        variant="ghost"
        disabled={award.isPending}
        onClick={() =>
          award.mutate({
            missionId,
            membershipId: member.membershipId,
            data: { awarded: true },
          })
        }
      >
        {t.missions.detailNameKnight}
      </Button>
      {error ? (
        <span role="alert" className="text-xs text-danger">
          {error}
        </span>
      ) : null}
    </div>
  );
}

/**
 * The mission's sweeps, each with what it found (§23.3).
 *
 * ⚠️ **The report is a column here rather than a screen of its own.** A round is read as one line of
 * the search's history — when it ran, over what ground, and what came of it — and sending an operator
 * to another page for the third of those is what made `report_id` a column nobody filled.
 */
function MissionRounds({ detail, locale }: { detail: MissionDetailDto; locale: 'ar' | 'en' }) {
  const { t } = useLocale();
  const { hasAny } = useRoles();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<MissionRoundDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const write = usePutApiV1MissionsMissionIdRoundsRoundIdReport({
    mutation: {
      onSuccess: async () => {
        setError(null);
        setEditing(null);
        await queryClient.invalidateQueries({
          queryKey: getGetApiV1MissionsMissionIdQueryKey(detail.missionId),
        });
      },
      onError: (cause: unknown) => setError(problemDetail(cause) ?? t.missions.actionFailed),
    },
  });

  // ⚠️ The button follows the domain rather than offering a click that is certain to fail: the server
  // answers 409 to a report on a sweep still under way, and again once the mission report is signed —
  // the filed PDF quotes these summaries, so they freeze at that signature.
  const canWrite = (round: MissionRoundDto) =>
    round.endedAt != null &&
    detail.report?.approvedAt == null &&
    hasAny(SystemRoles.DeskControl, SystemRoles.HeadOfControl, SystemRoles.SystemAdministrator);

  return (
    <Panel title={t.missions.detailRounds}>
      {detail.rounds.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t.missions.detailNoRounds}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="bg-surface-elevated text-xs uppercase tracking-wider text-muted-foreground">
              <TableHead scope="col">{t.missions.detailRoundSequence}</TableHead>
              <TableHead scope="col">{t.missions.detailRoundTitle}</TableHead>
              <TableHead scope="col">{t.missions.detailRoundArea}</TableHead>
              <TableHead scope="col">{t.missions.detailRoundStarted}</TableHead>
              <TableHead scope="col">{t.missions.detailRoundEnded}</TableHead>
              <TableHead scope="col">{t.missions.detailRoundReport}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {detail.rounds.map((round) => (
              <RoundRow
                key={round.roundId}
                round={round}
                locale={locale}
                canWrite={canWrite(round)}
                onWrite={() => {
                  setError(null);
                  setEditing(round);
                }}
              />
            ))}
          </TableBody>
        </Table>
      )}

      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}

      <MissionReportDialog
        open={editing != null}
        onOpenChange={(open) => setEditing(open ? editing : null)}
        title={t.missions.detailRoundReportDialogTitle}
        lede={t.missions.detailRoundReportDialogLede}
        initialSummary={editing?.reportSummary ?? ''}
        busy={write.isPending}
        serverError={error}
        onConfirm={(summary) => {
          if (editing) {
            write.mutate({
              missionId: detail.missionId,
              roundId: editing.roundId,
              data: { summary },
            });
          }
        }}
      />
    </Panel>
  );
}

function RoundRow({
  round,
  locale,
  canWrite,
  onWrite,
}: {
  round: MissionRoundDto;
  locale: 'ar' | 'en';
  canWrite: boolean;
  onWrite: () => void;
}) {
  const { t } = useLocale();

  return (
    <TableRow>
      <TableCell className="tabular font-medium">{round.sequence}</TableCell>
      <TableCell>{round.title}</TableCell>
      <TableCell className="tabular text-muted-foreground">
        {round.areaKm2.toFixed(1)} {t.missions.areaUnit}
      </TableCell>
      <TableCell className="tabular text-muted-foreground">
        {formatDateTime(round.startedAt, locale)}
      </TableCell>
      <TableCell className="tabular text-muted-foreground">
        {round.endedAt ? formatDateTime(round.endedAt, locale) : t.missions.detailRoundOpen}
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          {round.reportSummary ? (
            <>
              {/* Whole, not truncated: this is one or two sentences about what a sweep found, and a
                  clipped version would need a second click to answer the question the column asks. */}
              <p className="whitespace-pre-wrap text-sm">{round.reportSummary}</p>
              {round.reportPreparedByName ? (
                <span className="text-xs text-muted-foreground">
                  {t.missions.detailReportPreparedBy}: {round.reportPreparedByName}
                </span>
              ) : null}
            </>
          ) : (
            <span className="text-sm text-muted-foreground">
              {round.endedAt ? t.missions.detailRoundNoReport : '—'}
            </span>
          )}

          {/* ⚠️ Named for the ROUND, not "write the report". The closure panel on this same screen
              carries a button with the generic wording, and two controls reading «اكتب التقرير» on
              one page is a coin toss for anybody driving it by keyboard or screen reader — and it
              would write the wrong document. */}
          {canWrite ? (
            <Button variant="ghost" size="sm" onClick={onWrite}>
              {round.reportSummary
                ? t.missions.detailRoundReviseReport
                : t.missions.detailRoundWriteReport}
            </Button>
          ) : null}
        </div>
      </TableCell>
    </TableRow>
  );
}

function TimelineRow({ event, locale }: { event: MissionEventDto; locale: 'ar' | 'en' }) {
  const { t } = useLocale();

  return (
    <li className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border pb-2 text-sm last:border-0">
      <span className="tabular text-muted-foreground">
        {formatDateTime(event.occurredAt, locale)}
      </span>
      <span className="font-medium">
        {t.missionEvent[event.eventType as keyof typeof t.missionEvent] ?? event.eventType}
      </span>
      <span className="text-xs text-muted-foreground">
        {t.missions.detailTimelineActor}{' '}
        {/* A null actor is the system — the idle sweep removes members with no operator behind it
            (§23.5), and rendering an empty name there would read as missing data. */}
        {event.actorName ?? t.missions.detailTimelineSystem}
      </span>
    </li>
  );
}

function Figure({ label: name, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{name}</dt>
      <dd className="text-sm font-medium">{value}</dd>
    </div>
  );
}

function when(
  value: string | null | undefined,
  locale: 'ar' | 'en',
  t: { missions: { detailNone: string } },
): string {
  return value ? formatDateTime(value, locale) : t.missions.detailNone;
}

/**
 * The member's active time arrives as a plain second count, while `formatDuration` parses the
 * `TimeSpan` shape the rest of the API sends (`"1.06:12:30"`). Converted rather than given its own
 * formatter — one duration format in the panel, not two, and the floor-to-two-units rule the
 * shared one already encodes is the right rule here as well.
 */
function secondsToTimeSpan(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const days = Math.floor(total / 86_400);
  const hours = Math.floor((total % 86_400) / 3_600);
  const minutes = Math.floor((total % 3_600) / 60);
  const rest = total % 60;

  const clock = [hours, minutes, rest].map((part) => String(part).padStart(2, '0')).join(':');

  return days > 0 ? `${days}.${clock}` : clock;
}

/**
 * The API sends enum NAMES. An unknown key falls back to the raw value: if the server adds a status
 * before this panel knows about it, showing `Suspended` is honest and showing nothing hides meaning.
 */
function label(value: string, labels: Record<string, string>): string {
  return labels[value] ?? value;
}

/**
 * The members whose live position belongs on the map: the ones still on the mission.
 *
 * ⚠️ ⚠️ Not everybody in the members table. §5.9's map answers "where is my team now", and the
 * table deliberately keeps every membership the mission ever had so a removal can be reviewed and
 * a participation credited (§23.5, §23.6). Drawing a removed member's last position as a live one
 * is the worst kind of wrong answer a control room can be given.
 *
 * ⚠️ And §8.3 refuses a subscription to their track topic anyway — with `deny_action = disconnect`
 * that would cost the panel its whole broker connection, not just the request.
 */
function watchedMembers(detail: MissionDetailDto) {
  const onTheMission = new Set(['Joined', 'Active', 'IdleWarned']);

  return detail.members
    .filter((member) => onTheMission.has(member.state))
    .map((member) => ({ userId: member.userId, fullName: member.fullName }));
}
